import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../components/AuthProvider";
import biensService from "../services/biensService";
import BienDetailsPage from "./BienDetailsPage";
import { Bien } from "../types";
import { colors, sizes } from "../theme";
import { withMobilePrompt } from "../hoc/withMobilePrompt";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHome,
  faBuilding,
  faMap,
  faCar,
  faBox,
  faLock,
  faLightbulb,
  faFolder,
  faSearch,
  faEllipsisV,
  faEdit,
  faTrash,
  faShieldAlt,
  faFileAlt,
  faChartLine,
  faPlus,
  faAdd,
  faAlignJustify,
} from "@fortawesome/free-solid-svg-icons";
import { Image } from "react-native";
import { QRCodeCanvas } from "qrcode.react";

const CoffreFortPage: React.FC = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [biens, setBiens] = useState<Bien[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newBien, setNewBien] = useState({
    nom: "",
    description: "",
    type: "Maison" as Bien["type"],
  });
  const [selectedBien, setSelectedBien] = useState<Bien | null>(null);
  const [showBienMenu, setShowBienMenu] = useState<string | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingBien, setEditingBien] = useState<Bien | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [bienToDelete, setBienToDelete] = useState<Bien | null>(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  useEffect(() => {
    loadBiens();
  }, []);

  // Timer pour afficher l'onboarding automatiquement après 5 secondes si aucun bien
  useEffect(() => {
    if (
      !loading &&
      biens.length === 0 &&
      !localStorage.getItem("onboarding_completed")
    ) {
      const timer = setTimeout(() => {
        setShowOnboarding(true);
        setShowAddForm(true);
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [loading, biens.length]);

  const loadBiens = async () => {
    try {
      setLoading(true);
      const userBiens = await biensService.getBiensUtilisateur();
      setBiens(userBiens);
    } catch (error) {
      console.error("Erreur lors du chargement des biens:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddBien = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsCreating(true);
    setUploadingImage(true);

    try {
      let imageUrl =
        "https://firebasestorage.googleapis.com/v0/b/oosmose-properties.appspot.com/o/home.jpg?alt=media&token=ed011d2e-3b80-4973-b804-0f91b8a07e04";

      // Upload de l'image si sélectionnée
      if (selectedImage) {
        imageUrl = await biensService.uploadImage(selectedImage, (progress) => {
          setUploadProgress(progress);
        });
      }

      const newBienId = await biensService.ajouterBien({
        ...newBien,
        image: imageUrl,
      });

      setNewBien({ nom: "", description: "", type: "Maison" });
      setSelectedImage(null);
      setImagePreview(null);
      setUploadProgress(0);
      setShowAddForm(false);
      setShowOnboarding(false);
      localStorage.setItem("onboarding_completed", "true");

      // Rediriger automatiquement vers le nouveau bien
      navigate(`/bien-details/${newBienId}`);
    } catch (error: any) {
      alert(error.message);
    } finally {
      setIsCreating(false);
      setUploadingImage(false);
    }
  };

  const handleDeleteBien = async (bienId: string) => {
    if (window.confirm("Voulez-vous vraiment supprimer ce bien ?")) {
      try {
        await biensService.supprimerBien(bienId);
        loadBiens();
      } catch (error: any) {
        alert(error.message);
      }
    }
  };

  const handleEditBien = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingBien?.id) return;
    setUploadingImage(true);

    try {
      let imageUrl =
        editingBien.image ||
        "https://firebasestorage.googleapis.com/v0/b/oosmose-properties.appspot.com/o/home.jpg?alt=media&token=ed011d2e-3b80-4973-b804-0f91b8a07e04";

      // Upload de la nouvelle image si sélectionnée
      if (selectedImage) {
        imageUrl = await biensService.uploadImage(selectedImage, (progress) => {
          setUploadProgress(progress);
        });
      }

      await biensService.modifierBien(editingBien.id, {
        nom: editingBien.nom,
        type: editingBien.type,
        image: imageUrl,
      });

      setShowEditModal(false);
      setEditingBien(null);
      setSelectedImage(null);
      setImagePreview(null);
      setUploadProgress(0);
      loadBiens();
    } catch (error: any) {
      alert(error.message);
    } finally {
      setUploadingImage(false);
    }
  };

  const confirmDelete = async () => {
    if (!bienToDelete?.id) return;

    try {
      await biensService.supprimerBien(bienToDelete.id);
      setShowDeleteModal(false);
      setBienToDelete(null);
      setShowBienMenu(null);
      loadBiens();
    } catch (error: any) {
      alert(error.message);
    }
  };

  const getIconForType = (type: string) => {
    switch (type) {
      case "Maison":
        return <FontAwesomeIcon icon={faHome} />;
      case "Appartement":
        return <FontAwesomeIcon icon={faBuilding} />;
      case "Terrain":
        return <FontAwesomeIcon icon={faMap} />;
      case "Garage":
        return <FontAwesomeIcon icon={faCar} />;
      case "Autre":
        return <FontAwesomeIcon icon={faBox} />;
      default:
        return <FontAwesomeIcon icon={faHome} />;
    }
  };

  const styles = {
    container: {
      minHeight: "100vh",
      background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primary} 100%)`,
    },
    header: {
      background: "transparent",
      padding: "50px 20px 80px",
      color: colors.white,
      textAlign: "center" as const,
      position: "relative" as const,
      overflow: "visible",
    },
    headerPattern: {
      position: "absolute" as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      opacity: 0.1,
      backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Ccircle cx='30' cy='30' r='4'/%3E%3Ccircle cx='10' cy='30' r='2'/%3E%3Ccircle cx='50' cy='30' r='2'/%3E%3Ccircle cx='30' cy='10' r='2'/%3E%3Ccircle cx='30' cy='50' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
    },
    headerTitle: {
      fontSize: `${sizes.h1}px`,
      fontWeight: "bold",
      marginBottom: "15px",
      marginTop: "25px",
      position: "relative" as const,
      zIndex: 1,
    },
    headerSubtitle: {
      fontSize: `${sizes.h5}px`,
      opacity: 0.95,
      position: "relative" as const,
      zIndex: 1,
    },
    userInfo: {
      position: "absolute" as const,
      top: "20px",
      right: "20px",
      display: "flex",
      alignItems: "center",
      gap: "15px",
    },
    userInfo2: {
      position: "absolute" as const,
      top: "0px",
      left: "20px",
      display: "flex",
      alignItems: "center",
      gap: "15px",
    },
    userEmail: {
      fontSize: "14px",
      opacity: 0.9,
    },
    logoutButton: {
      padding: "8px 16px",
      backgroundColor: "rgba(255,255,255,0.2)",
      border: "1px solid rgba(255,255,255,0.3)",
      borderRadius: "20px",
      color: colors.white,
      cursor: "pointer",
      fontSize: "14px",
    },
    content: {
      margin: "0px auto 0",
      padding: "0 20px 40px",
      position: "relative" as const,
      zIndex: 2,
    },
    contentCard: {
      backgroundColor: "rgba(255, 255, 255, 0.2)",
      borderRadius: "24px",
      padding: "40px 32px",
      border: "2px solid rgba(255, 255, 255, 0.3)",
      boxShadow: "0 -10px 40px rgba(0,0,0,0.1)",
    },
    section: {
      marginBottom: "0px",
    },
    sectionHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "20px",
    },
    sectionTitle: {
      fontSize: `${sizes.h4}px`,
      fontWeight: "bold",
      color: colors.white,
    },
    diu: {
      fontSize: `14px`,
      color: colors.white,
      marginTop: "-20px",
      marginBottom: "20px",
      opacity: 0.9,
    },
    addButton: {
      padding: "12px 24px",
      background: colors.black,
      color: colors.white,
      border: "none",
      borderRadius: "12px",
      cursor: "pointer",
      fontSize: "15px",
      fontWeight: "bold",
      boxShadow: "0 2px 12px rgba(180, 132, 14, 0.25)",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    },
    biensGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
      gap: "24px",
    },
    bienCard: {
      backgroundColor: colors.white,
      borderRadius: "20px",
      padding: "28px",
      boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
      cursor: "pointer",
      transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
      border: "1px solid rgba(0,0,0,0.04)",
      position: "relative" as const,
      overflow: "hidden",
    },
    bienCardHover: {
      transform: "translateY(-6px)",
      boxShadow: "0 8px 24px rgba(180, 132, 14, 0.15)",
      borderColor: colors.primary,
    },
    bienCardShine: {
      position: "absolute" as const,
      top: 0,
      left: "-100%",
      width: "100%",
      height: "100%",
      background:
        "linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)",
      transition: "left 0.5s ease",
    },
    bienIcon: {
      fontSize: "40px",
      textAlign: "center" as const,
      marginBottom: 10,
    },
    bienName: {
      fontSize: `${sizes.h4}px`,
      fontWeight: "bold",
      color: colors.text,
      marginBottom: "12px",
      textAlign: "center" as const,
    },
    bienDescription: {
      fontSize: `${sizes.text}px`,
      color: colors.gray,
      marginBottom: "20px",
      textAlign: "center" as const,
      lineHeight: "1.5",
    },
    bienType: {
      fontSize: "13px",
      background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.warning} 100%)`,
      color: colors.white,
      padding: "6px 12px",
      borderRadius: "20px",
      display: "inline-block",
      fontWeight: "bold",
      textAlign: "center" as const,
      width: "100%",
    },
    deleteButton: {
      position: "absolute" as const,
      top: "12px",
      right: "12px",
      background: "rgba(239, 68, 68, 0.1)",
      border: "none",
      fontSize: "16px",
      cursor: "pointer",
      opacity: 0.7,
      transition: "all 0.3s ease",
      width: "32px",
      height: "32px",
      borderRadius: "8px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    emptyState: {
      textAlign: "center" as const,
      color: colors.white,
    },
    emptyIcon: {},
    emptyTitle: {
      fontSize: `${sizes.h4}px`,
      fontWeight: "bold",
      marginBottom: "10px",
    },
    emptyText: {
      fontSize: `${sizes.p}px`,
      marginBottom: "30px",
    },
    modal: {
      position: "fixed" as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0,0,0,0.5)",
      backdropFilter: "blur(4px)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1000,
      animation: "fadeIn 0.3s ease",
    },
    modalContent: {
      backgroundColor: colors.white,
      borderRadius: "20px",
      padding: "32px",
      width: "90%",
      maxWidth: "500px",
      maxHeight: "90vh",
      overflow: "auto",
      boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
      animation: "slideUp 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    },
    modalTitle: {
      fontSize: `${sizes.h4}px`,
      fontWeight: "bold",
      marginBottom: "20px",
      color: colors.text,
    },
    form: {
      display: "flex",
      flexDirection: "column" as const,
      gap: "20px",
    },
    inputGroup: {
      display: "flex",
      flexDirection: "column" as const,
      gap: "8px",
    },
    label: {
      fontSize: `${sizes.text}px`,
      fontWeight: "500",
      color: colors.text,
    },
    input: {
      height: `${sizes.inputHeight}px`,
      padding: `0 ${sizes.inputPadding}px`,
      border: "1px solid rgba(0,0,0,0.1)",
      borderRadius: "12px",
      fontSize: `${sizes.text}px`,
      outline: "none",
      transition: "all 0.3s ease",
    },
    select: {
      height: `${sizes.inputHeight}px`,
      padding: `0 ${sizes.inputPadding}px`,
      border: "1px solid rgba(0,0,0,0.1)",
      borderRadius: "12px",
      fontSize: `${sizes.text}px`,
      outline: "none",
      backgroundColor: colors.white,
      transition: "all 0.3s ease",
    },
    textarea: {
      minHeight: "100px",
      padding: `${sizes.inputPadding}px`,
      border: "1px solid rgba(0,0,0,0.1)",
      borderRadius: "12px",
      fontSize: `${sizes.text}px`,
      outline: "none",
      resize: "vertical" as const,
      fontFamily: "inherit",
      transition: "all 0.3s ease",
    },
    buttonGroup: {
      display: "flex",
      gap: "10px",
      justifyContent: "flex-end",
    },
    cancelButton: {
      padding: "12px 24px",
      backgroundColor: colors.gray,
      color: colors.white,
      border: "none",
      borderRadius: "12px",
      cursor: "pointer",
      fontWeight: "600",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    },
    submitButton: {
      padding: "12px 24px",
      background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.warning} 100%)`,
      color: colors.white,
      border: "none",
      borderRadius: "12px",
      cursor: "pointer",
      fontWeight: "600",
      boxShadow: "0 2px 12px rgba(180, 132, 14, 0.25)",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    },
    loader: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "200px",
      fontSize: "18px",
      color: colors.gray,
    },
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loader}>Chargement de vos biens...</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerPattern}></div>
        <div style={styles.userInfo2}>
          <img
            src="/logo-white.png"
            alt="Logo"
            style={{ width: "70px", height: "70px" }}
          />
        </div>
        <div style={styles.userInfo}>
          <span style={styles.userEmail}>{user?.email}</span>
          <button style={styles.logoutButton} onClick={signOut}>
            Déconnexion
          </button>
        </div>
        <div style={styles.headerTitle}>
          <FontAwesomeIcon icon={faLock} style={{ marginRight: "12px" }} />
          <br></br>
          Votre Coffre-Fort
        </div>
        <div style={styles.headerSubtitle}>
          Le Carnet de Vie de votre Maison
        </div>
      </div>

      {/* Content */}
      <div style={styles.content}>
        <p style={styles.diu}>
          Constituer un Dossier d'Intervention Ultérieure (Belgique) ou Carnet
          d'Information du Logement (France) est une obligation depuis,
          respectivement, 2001 et 2023.
        </p>
        <div style={styles.contentCard}>
          {/* Section Biens */}
          <div style={styles.section}>
            <div style={styles.sectionHeader}>
              <h2 style={styles.sectionTitle}>
                {biens.length > 1
                  ? "Vos biens"
                  : biens.length === 1
                  ? `${biens.length} bien${biens.length === 1 ? "" : "s"}`
                  : ""}{" "}
                <FontAwesomeIcon
                  icon={faHome}
                  style={{ marginLeft: "2px", color: colors.white }}
                />
              </h2>
              <button
                style={styles.addButton}
                onClick={() => setShowAddForm(true)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow =
                    "0 6px 20px rgba(180, 132, 14, 0.4)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "none";
                  e.currentTarget.style.boxShadow =
                    "0 4px 12px rgba(180, 132, 14, 0.3)";
                }}
              >
                Ajouter{" "}
                <FontAwesomeIcon
                  icon={faAdd}
                  style={{ marginLeft: "8px", color: colors.white }}
                />
              </button>
            </div>

            {biens.length === 0 ? (
              <div style={styles.emptyState}>
                <div style={styles.emptyIcon}>
                  <img
                    src="/logo-white.png"
                    alt="Logo"
                    style={{
                      width: "200px",
                      height: "200px",
                      marginTop: "-40px",
                    }}
                  />
                </div>
                <div style={styles.emptyTitle}>
                  Bienvenue sur Property Assist ! 🏡
                </div>
                <div style={styles.emptyText}>
                  Commencez votre organisation dès maintenant en ajoutant votre
                  premier bien immobilier.
                  <br />
                  <br />
                  Nous vous guiderons pas à pas pour vous aider à organiser tous
                  vos documents importants.
                </div>
                <button
                  style={{
                    ...styles.addButton,
                    fontSize: "18px",
                    padding: "16px 32px",
                    marginTop: "24px",
                  }}
                  onClick={() => {
                    setShowOnboarding(true);
                    setShowAddForm(true);
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform =
                      "translateY(-2px) scale(1.05)";
                    e.currentTarget.style.boxShadow =
                      "0 8px 24px rgba(180, 132, 14, 0.4)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "none";
                    e.currentTarget.style.boxShadow =
                      "0 4px 12px rgba(180, 132, 14, 0.3)";
                  }}
                >
                  Ajouter mon premier bien{" "}
                  <FontAwesomeIcon
                    icon={faPlus}
                    style={{ marginLeft: "8px", color: colors.white }}
                  />
                </button>
              </div>
            ) : (
              <div style={styles.biensGrid} className="biensGrid">
                {biens.map((bien) => (
                  <div
                    key={bien.id}
                    style={{
                      ...styles.bienCard,
                      position: "relative",
                    }}
                    onClick={() => navigate(`/bien-details/${bien.id}`)}
                    onMouseEnter={(e) => {
                      Object.assign(
                        e.currentTarget.style,
                        styles.bienCardHover
                      );
                      const shine = e.currentTarget.querySelector(
                        ".shine"
                      ) as HTMLElement;
                      if (shine) shine.style.left = "100%";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "";
                      e.currentTarget.style.boxShadow =
                        "0 6px 20px rgba(0,0,0,0.08)";
                      e.currentTarget.style.borderColor = "rgba(0,0,0,0.05)";
                      const shine = e.currentTarget.querySelector(
                        ".shine"
                      ) as HTMLElement;
                      if (shine) shine.style.left = "-100%";
                    }}
                  >
                    <div className="shine" style={styles.bienCardShine}></div>

                    {/* Menu 3 points */}
                    <div
                      style={{
                        position: "absolute",
                        top: "16px",
                        right: "16px",
                        zIndex: 10,
                      }}
                    >
                      <button
                        style={{
                          width: "36px",
                          height: "36px",
                          borderRadius: "50%",
                          border: "none",
                          background: "rgba(255, 255, 255, 0.9)",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                          transition: "all 0.2s",
                          fontSize: "18px",
                          color: colors.text,
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowBienMenu(
                            showBienMenu === bien.id ? null : bien.id || null
                          );
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = colors.light;
                          e.currentTarget.style.transform = "scale(1.1)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background =
                            "rgba(255, 255, 255, 0.9)";
                          e.currentTarget.style.transform = "scale(1)";
                        }}
                      >
                        <FontAwesomeIcon icon={faEllipsisV} />
                      </button>

                      {/* Menu déroulant */}
                      {showBienMenu === bien.id && (
                        <div
                          style={{
                            position: "absolute",
                            top: "44px",
                            right: "0",
                            background: colors.white,
                            borderRadius: "12px",
                            boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
                            overflow: "hidden",
                            minWidth: "180px",
                            zIndex: 1000,
                          }}
                        >
                          <button
                            style={{
                              width: "100%",
                              padding: "12px 16px",
                              border: "none",
                              background: "transparent",
                              textAlign: "left",
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              gap: "12px",
                              fontSize: "14px",
                              color: colors.text,
                              transition: "background 0.2s",
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingBien(bien);
                              setShowEditModal(true);
                              setShowBienMenu(null);
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = colors.light;
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = "transparent";
                            }}
                          >
                            <FontAwesomeIcon
                              icon={faEdit}
                              style={{ color: colors.primary }}
                            />
                            Modifier le bien
                          </button>
                          <button
                            style={{
                              width: "100%",
                              padding: "12px 16px",
                              border: "none",
                              background: "transparent",
                              textAlign: "left",
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              gap: "12px",
                              fontSize: "14px",
                              color: "#dc2626",
                              transition: "background 0.2s",
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setBienToDelete(bien);
                              setShowDeleteModal(true);
                              setShowBienMenu(null);
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background =
                                "rgba(239, 68, 68, 0.1)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = "transparent";
                            }}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                            Supprimer le bien
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Image du bien */}
                    <div
                      style={{
                        width: "100%",
                        height: "200px",
                        borderRadius: "12px",
                        overflow: "hidden",
                        position: "relative",
                        backgroundColor: "#f3f4f6",
                      }}
                    >
                      <img
                        src={bien.image || "/home.jpg"}
                        alt={bien.nom}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = "/home.jpg";
                        }}
                      />
                      {/* Overlay gradient pour meilleure lisibilité */}
                      <div
                        style={{
                          position: "absolute",
                          bottom: 0,
                          left: 0,
                          right: 0,
                          height: "60px",
                          background:
                            "linear-gradient(to top, rgba(0,0,0,0.6) 0%, transparent 100%)",
                        }}
                      ></div>
                    </div>

                    <div style={{ padding: "20px" }}>
                      <div style={styles.bienName}>{bien.nom}</div>
                      <div style={styles.bienDescription}>
                        {bien.description}
                      </div>
                      <div style={styles.bienType}>
                        {getIconForType(bien.type)} {bien.type}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Section Aide & Conseils - En dehors de la card */}
      <div
        style={{
          margin: "0 auto",
          padding: "0px 20px 40px",
        }}
      >
        <h2
          style={{
            fontSize: "36px",
            fontWeight: "800",
            color: colors.white,
            textAlign: "center",
            marginBottom: "48px",
            textShadow: "0 2px 10px rgba(0,0,0,0.2)",
          }}
        >
          <FontAwesomeIcon icon={faLightbulb} style={{ marginRight: "16px" }} />
          Comment ça marche ?
        </h2>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: "24px",
          }}
        >
          {/* Card 1 - Ajouter */}
          <div
            style={{
              background: colors.white,
              borderRadius: "20px",
              padding: "32px",
              boxShadow: "0 8px 30px rgba(0,0,0,0.12)",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              cursor: "pointer",
              textAlign: "center",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-8px)";
              e.currentTarget.style.boxShadow = "0 12px 40px rgba(0,0,0,0.18)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 8px 30px rgba(0,0,0,0.12)";
            }}
          >
            <div
              style={{
                width: "80px",
                height: "80px",
                background: `linear-gradient(135deg, ${colors.black}, ${colors.black})`,
                borderRadius: "20px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 24px",
                fontSize: "36px",
                color: colors.white,
                boxShadow: "0 8px 20px rgba(180, 132, 14, 0.3)",
              }}
            >
              <FontAwesomeIcon icon={faHome} />
            </div>
            <h3
              style={{
                fontSize: "20px",
                fontWeight: "700",
                color: colors.text,
                marginBottom: "12px",
              }}
            >
              Ajoutez vos biens
            </h3>
            <p
              style={{
                fontSize: "15px",
                color: colors.gray,
                lineHeight: "1.7",
                margin: 0,
              }}
            >
              Créez des profils pour chacun de vos biens immobiliers et
              centralisez toutes les informations importantes
            </p>
          </div>

          {/* Card 2 - Organiser */}
          <div
            style={{
              background: colors.white,
              borderRadius: "20px",
              padding: "32px",
              boxShadow: "0 8px 30px rgba(0,0,0,0.12)",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              cursor: "pointer",
              textAlign: "center",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-8px)";
              e.currentTarget.style.boxShadow = "0 12px 40px rgba(0,0,0,0.18)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 8px 30px rgba(0,0,0,0.12)";
            }}
          >
            <div
              style={{
                width: "80px",
                height: "80px",
                background: `linear-gradient(135deg, ${colors.black}, ${colors.black})`,
                borderRadius: "20px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 24px",
                fontSize: "36px",
                color: colors.white,
                boxShadow: "0 8px 20px rgba(59, 130, 246, 0.3)",
              }}
            >
              <FontAwesomeIcon icon={faFolder} />
            </div>
            <h3
              style={{
                fontSize: "20px",
                fontWeight: "700",
                color: colors.text,
                marginBottom: "12px",
              }}
            >
              Organisez vos documents
            </h3>
            <p
              style={{
                fontSize: "15px",
                color: colors.gray,
                lineHeight: "1.7",
                margin: 0,
              }}
            >
              Classez vos documents dans des dossiers personnalisés et
              retrouvez-les en un clin d'œil quand vous en avez besoin
            </p>
          </div>

          {/* Card 3 - Sécurité */}
          <div
            style={{
              background: colors.white,
              borderRadius: "20px",
              padding: "32px",
              boxShadow: "0 8px 30px rgba(0,0,0,0.12)",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              cursor: "pointer",
              textAlign: "center",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-8px)";
              e.currentTarget.style.boxShadow = "0 12px 40px rgba(0,0,0,0.18)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 8px 30px rgba(0,0,0,0.12)";
            }}
          >
            <div
              style={{
                width: "80px",
                height: "80px",
                background: `linear-gradient(135deg, ${colors.black}, ${colors.black})`,
                borderRadius: "20px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 24px",
                fontSize: "36px",
                color: colors.white,
                boxShadow: "0 8px 20px rgba(16, 185, 129, 0.3)",
              }}
            >
              <FontAwesomeIcon icon={faShieldAlt} />
            </div>
            <h3
              style={{
                fontSize: "20px",
                fontWeight: "700",
                color: colors.text,
                marginBottom: "12px",
              }}
            >
              Sécurité garantie
            </h3>
            <p
              style={{
                fontSize: "15px",
                color: colors.gray,
                lineHeight: "1.7",
                margin: 0,
              }}
            >
              Vos données sont cryptées et stockées en toute sécurité. Vous seul
              avez accès à vos informations personnelles
            </p>
          </div>

          {/* Card 4 - Accès rapide */}
          <div
            style={{
              background: colors.white,
              borderRadius: "20px",
              padding: "32px",
              boxShadow: "0 8px 30px rgba(0,0,0,0.12)",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              cursor: "pointer",
              textAlign: "center",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-8px)";
              e.currentTarget.style.boxShadow = "0 12px 40px rgba(0,0,0,0.18)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 8px 30px rgba(0,0,0,0.12)";
            }}
          >
            <div
              style={{
                width: "80px",
                height: "80px",
                background: `linear-gradient(135deg, ${colors.black}, ${colors.black})`,
                borderRadius: "20px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 24px",
                fontSize: "36px",
                color: colors.white,
                boxShadow: "0 8px 20px rgba(245, 158, 11, 0.3)",
              }}
            >
              <FontAwesomeIcon icon={faChartLine} />
            </div>
            <h3
              style={{
                fontSize: "20px",
                fontWeight: "700",
                color: colors.text,
                marginBottom: "12px",
              }}
            >
              Suivez l'évolution
            </h3>
            <p
              style={{
                fontSize: "15px",
                color: colors.gray,
                lineHeight: "1.7",
                margin: 0,
              }}
            >
              Gardez une trace de tous les événements et travaux effectués pour
              une vision complète de votre patrimoine
            </p>
          </div>
        </div>
      </div>

      {/* Section Téléchargements - En dehors de la card */}
      <div style={styles.content}>
        <div
          id="telechargements"
          style={{
            background: colors.white,
            borderRadius: "24px",
            padding: "48px",
            margin: "0 auto 0px",
            boxShadow: "0 12px 40px rgba(0,0,0,0.12)",
            textAlign: "center",
          }}
        >
          <h2
            style={{
              fontSize: "32px",
              fontWeight: "800",
              color: colors.text,
              marginBottom: "16px",
            }}
          >
            Accédez à vos biens partout
          </h2>
          <p
            style={{
              fontSize: "18px",
              color: colors.gray,
              lineHeight: "1.7",
              marginBottom: "48px",
              maxWidth: "700px",
              margin: "0 auto 48px",
            }}
          >
            Téléchargez l'application mobile pour gérer vos documents, recevoir
            des rappels et scanner des fichiers où que vous soyez.
          </p>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
              gap: "32px",
              justifyContent: "center",
              maxWidth: "800px",
              margin: "0 auto",
            }}
          >
            {/* QR Code iOS */}
            <div
              style={{
                padding: "24px",
                backgroundColor: "#f9fafb",
                borderRadius: "20px",
                border: "1px solid #f3f4f6",
                transition: "all 0.3s ease",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "space-between",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-5px)";
                e.currentTarget.style.boxShadow =
                  "0 10px 25px rgba(0,0,0,0.08)";
                e.currentTarget.style.borderColor = "rgba(0,0,0,0.08)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "none";
                e.currentTarget.style.borderColor = "#f3f4f6";
              }}
            >
              <h3
                style={{
                  fontSize: "22px",
                  fontWeight: "700",
                  color: colors.text,
                  marginBottom: "20px",
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                }}
              >
                <svg
                  width="28"
                  height="28"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                </svg>
                App Store
              </h3>
              <div
                style={{
                  padding: "12px",
                  background: "white",
                  borderRadius: "12px",
                  boxShadow: "0 4px 15px rgba(0,0,0,0.07)",
                  marginBottom: "24px",
                }}
              >
                <QRCodeCanvas
                  value={
                    "https://apps.apple.com/be/app/property-assist/id6450917128"
                  }
                  size={180}
                  bgColor={"#ffffff"}
                  fgColor={"#000000"}
                  level={"L"}
                  includeMargin={false}
                />
              </div>
              <a
                href="https://apps.apple.com/be/app/property-assist/id6450917128"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  textDecoration: "none",
                  color: colors.primary,
                  fontWeight: "600",
                  fontSize: "15px",
                  padding: "10px 20px",
                  borderRadius: "10px",
                  background: "rgba(180, 132, 14, 0.1)",
                  transition: "all 0.3s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "rgba(180, 132, 14, 0.2)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "rgba(180, 132, 14, 0.1)";
                }}
              >
                Ouvrir l'App Store
              </a>
            </div>

            {/* QR Code Android */}
            <div
              style={{
                padding: "24px",
                backgroundColor: "#f9fafb",
                borderRadius: "20px",
                border: "1px solid #f3f4f6",
                transition: "all 0.3s ease",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "space-between",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-5px)";
                e.currentTarget.style.boxShadow =
                  "0 10px 25px rgba(0,0,0,0.08)";
                e.currentTarget.style.borderColor = "rgba(0,0,0,0.08)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "none";
                e.currentTarget.style.borderColor = "#f3f4f6";
              }}
            >
              <h3
                style={{
                  fontSize: "22px",
                  fontWeight: "700",
                  color: colors.text,
                  marginBottom: "20px",
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                }}
              >
                <svg
                  width="28"
                  height="28"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.5,12.92 20.16,13.19L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
                </svg>
                Google Play
              </h3>
              <div
                style={{
                  padding: "12px",
                  background: "white",
                  borderRadius: "12px",
                  boxShadow: "0 4px 15px rgba(0,0,0,0.07)",
                  marginBottom: "24px",
                }}
              >
                <QRCodeCanvas
                  value={
                    "https://play.google.com/store/apps/details?id=com.louisv2304.oosmoseproperties"
                  }
                  size={180}
                  bgColor={"#ffffff"}
                  fgColor={"#000000"}
                  level={"L"}
                  includeMargin={false}
                />
              </div>
              <a
                href="https://play.google.com/store/apps/details?id=com.louisv2304.oosmoseproperties"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  textDecoration: "none",
                  color: colors.primary,
                  fontWeight: "600",
                  fontSize: "15px",
                  padding: "10px 20px",
                  borderRadius: "10px",
                  background: "rgba(180, 132, 14, 0.1)",
                  transition: "all 0.3s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "rgba(180, 132, 14, 0.2)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "rgba(180, 132, 14, 0.1)";
                }}
              >
                Ouvrir le Play Store
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Modal d'ajout de bien */}
      {showAddForm && (
        <div style={styles.modal}>
          <div style={styles.modalContent}>
            <h3 style={styles.modalTitle}>
              Ajouter un nouveau bien{" "}
              <FontAwesomeIcon
                icon={faPlus}
                style={{ marginLeft: "8px", color: colors.white }}
              />
            </h3>
            <form onSubmit={handleAddBien} style={styles.form}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Nom du bien</label>
                <input
                  type="text"
                  value={newBien.nom}
                  onChange={(e) =>
                    setNewBien({ ...newBien, nom: e.target.value })
                  }
                  style={styles.input}
                  placeholder="Ex: Ma maison principale"
                  onFocus={(e) =>
                    (e.currentTarget.style.borderColor = colors.primary)
                  }
                  onBlur={(e) =>
                    (e.currentTarget.style.borderColor = "rgba(0,0,0,0.1)")
                  }
                  required
                />
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}>Description</label>
                <textarea
                  value={newBien.description}
                  onChange={(e) =>
                    setNewBien({ ...newBien, description: e.target.value })
                  }
                  style={styles.textarea}
                  placeholder="Ex: Maison familiale de 120m² avec jardin"
                  required
                />
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}>Type de bien</label>
                <select
                  value={newBien.type}
                  onChange={(e) =>
                    setNewBien({
                      ...newBien,
                      type: e.target.value as Bien["type"],
                    })
                  }
                  style={styles.select}
                  required
                >
                  <option value="Maison">Maison</option>
                  <option value="Appartement">Appartement</option>
                  <option value="Terrain">Terrain</option>
                  <option value="Garage">Garage</option>
                  <option value="Autre">Autre</option>
                </select>
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}>Image du bien (optionnelle)</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      setSelectedImage(file);
                      setImagePreview(URL.createObjectURL(file));
                    }
                  }}
                  style={styles.input}
                />
                {imagePreview && (
                  <div style={{ marginTop: "10px", textAlign: "center" }}>
                    <img
                      src={imagePreview}
                      alt="Aperçu"
                      style={{
                        maxWidth: "200px",
                        maxHeight: "200px",
                        borderRadius: "8px",
                        objectFit: "cover",
                      }}
                    />
                  </div>
                )}
                {uploadingImage && (
                  <div style={{ marginTop: "10px" }}>
                    <div
                      style={{
                        width: "100%",
                        height: "4px",
                        backgroundColor: "rgba(0,0,0,0.1)",
                        borderRadius: "2px",
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          width: `${uploadProgress}%`,
                          height: "100%",
                          backgroundColor: colors.primary,
                          transition: "width 0.3s ease",
                        }}
                      />
                    </div>
                    <p
                      style={{
                        fontSize: "12px",
                        color: colors.text,
                        marginTop: "4px",
                        textAlign: "center",
                      }}
                    >
                      Envoi en cours... {Math.round(uploadProgress)}%
                    </p>
                  </div>
                )}
              </div>

              <div style={styles.buttonGroup}>
                <button
                  type="button"
                  style={styles.cancelButton}
                  onClick={() => {
                    setShowAddForm(false);
                    setNewBien({ nom: "", description: "", type: "Maison" });
                    setSelectedImage(null);
                    setImagePreview(null);
                    setUploadProgress(0);
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow =
                      "0 4px 12px rgba(0,0,0,0.15)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "none";
                    e.currentTarget.style.boxShadow = "none";
                  }}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  style={{
                    ...styles.submitButton,
                    opacity: isCreating ? 0.6 : 1,
                    cursor: isCreating ? "not-allowed" : "pointer",
                  }}
                  disabled={isCreating}
                  onMouseEnter={(e) => {
                    if (!isCreating) {
                      e.currentTarget.style.transform = "translateY(-2px)";
                      e.currentTarget.style.boxShadow =
                        "0 4px 20px rgba(180, 132, 14, 0.4)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isCreating) {
                      e.currentTarget.style.transform = "none";
                      e.currentTarget.style.boxShadow =
                        "0 2px 12px rgba(180, 132, 14, 0.25)";
                    }
                  }}
                >
                  {isCreating ? "Création en cours..." : "Ajouter"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de modification */}
      {showEditModal && editingBien && (
        <div style={styles.modal}>
          <div style={styles.modalContent}>
            <h3 style={styles.modalTitle}>Modifier le bien</h3>
            <form onSubmit={handleEditBien} style={styles.form}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Nom du bien</label>
                <input
                  type="text"
                  value={editingBien?.nom || ""}
                  onChange={(e) =>
                    editingBien &&
                    setEditingBien({ ...editingBien, nom: e.target.value })
                  }
                  style={styles.input}
                  placeholder="Ex: Ma maison principale"
                  onFocus={(e) =>
                    (e.currentTarget.style.borderColor = colors.primary)
                  }
                  onBlur={(e) =>
                    (e.currentTarget.style.borderColor = "rgba(0,0,0,0.1)")
                  }
                  required
                />
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}>Type de bien</label>
                <select
                  value={editingBien?.type || ""}
                  onChange={(e) =>
                    editingBien &&
                    setEditingBien({
                      ...editingBien,
                      type: e.target.value as Bien["type"],
                    })
                  }
                  style={styles.select}
                  required
                >
                  <option value="Maison">Maison</option>
                  <option value="Appartement">Appartement</option>
                  <option value="Terrain">Terrain</option>
                  <option value="Garage">Garage</option>
                  <option value="Autre">Autre</option>
                </select>
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}>Image du bien</label>
                {editingBien?.image && !imagePreview && (
                  <div style={{ marginBottom: "10px", textAlign: "center" }}>
                    <img
                      src={editingBien.image}
                      alt="Image actuelle"
                      style={{
                        maxWidth: "200px",
                        maxHeight: "200px",
                        borderRadius: "8px",
                        objectFit: "cover",
                      }}
                    />
                    <p
                      style={{
                        fontSize: "12px",
                        color: colors.gray,
                        marginTop: "4px",
                      }}
                    >
                      Image actuelle
                    </p>
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      setSelectedImage(file);
                      setImagePreview(URL.createObjectURL(file));
                    }
                  }}
                  style={styles.input}
                />
                {imagePreview && (
                  <div style={{ marginTop: "10px", textAlign: "center" }}>
                    <img
                      src={imagePreview}
                      alt="Nouvelle image"
                      style={{
                        maxWidth: "200px",
                        maxHeight: "200px",
                        borderRadius: "8px",
                        objectFit: "cover",
                      }}
                    />
                    <p
                      style={{
                        fontSize: "12px",
                        color: colors.primary,
                        marginTop: "4px",
                      }}
                    >
                      Nouvelle image (sera envoyée lors de l'enregistrement)
                    </p>
                  </div>
                )}
                {uploadingImage && (
                  <div style={{ marginTop: "10px" }}>
                    <div
                      style={{
                        width: "100%",
                        height: "4px",
                        backgroundColor: "rgba(0,0,0,0.1)",
                        borderRadius: "2px",
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          width: `${uploadProgress}%`,
                          height: "100%",
                          backgroundColor: colors.primary,
                          transition: "width 0.3s ease",
                        }}
                      />
                    </div>
                    <p
                      style={{
                        fontSize: "12px",
                        color: colors.text,
                        marginTop: "4px",
                        textAlign: "center",
                      }}
                    >
                      Envoi en cours... {Math.round(uploadProgress)}%
                    </p>
                  </div>
                )}
              </div>

              <div style={styles.buttonGroup}>
                <button
                  type="button"
                  style={styles.cancelButton}
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingBien(null);
                    setSelectedImage(null);
                    setImagePreview(null);
                    setUploadProgress(0);
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow =
                      "0 4px 12px rgba(0,0,0,0.15)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = "none";
                  }}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  style={styles.submitButton}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow =
                      "0 6px 20px rgba(180, 132, 14, 0.4)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "none";
                    e.currentTarget.style.boxShadow =
                      "0 2px 12px rgba(180, 132, 14, 0.25)";
                  }}
                >
                  Enregistrer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de confirmation de suppression */}
      {showDeleteModal && bienToDelete && (
        <div style={styles.modal}>
          <div
            style={{
              ...styles.modalContent,
              maxWidth: "450px",
              textAlign: "center",
            }}
          >
            <div
              style={{
                width: "80px",
                height: "80px",
                background: "rgba(239, 68, 68, 0.1)",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 24px",
                fontSize: "36px",
                color: "#dc2626",
              }}
            >
              <FontAwesomeIcon icon={faTrash} />
            </div>
            <h3 style={{ ...styles.modalTitle, marginBottom: "16px" }}>
              Confirmer la suppression
            </h3>
            <p
              style={{
                fontSize: "16px",
                color: colors.gray,
                lineHeight: "1.7",
                marginBottom: "24px",
              }}
            >
              Êtes-vous sûr de vouloir supprimer{" "}
              <strong style={{ color: colors.text }}>
                "{bienToDelete?.nom}"
              </strong>{" "}
              ?
              <br />
              <br />
              <span style={{ color: "#dc2626", fontWeight: "600" }}>
                Cette action est irréversible.
              </span>
            </p>
            <div style={styles.buttonGroup}>
              <button
                type="button"
                style={styles.cancelButton}
                onClick={() => {
                  setShowDeleteModal(false);
                  setBienToDelete(null);
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow =
                    "0 4px 12px rgba(0,0,0,0.15)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                Annuler
              </button>
              <button
                type="button"
                style={{
                  ...styles.submitButton,
                  background: "#dc2626",
                }}
                onClick={confirmDelete}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow =
                    "0 6px 20px rgba(220, 38, 38, 0.4)";
                  e.currentTarget.style.background = "#b91c1c";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "none";
                  e.currentTarget.style.boxShadow =
                    "0 2px 12px rgba(220, 38, 38, 0.25)";
                  e.currentTarget.style.background = "#dc2626";
                }}
              >
                Supprimer définitivement
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de détails du bien */}
      {selectedBien && (
        <BienDetailsPage
          bien={selectedBien || undefined}
          onClose={() => setSelectedBien(null)}
        />
      )}

      {/* Modal d'onboarding */}
      {showOnboarding && biens.length === 0 && showAddForm && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10000,
            padding: "20px",
          }}
        >
          <div
            style={{
              backgroundColor: colors.white,
              borderRadius: "24px",
              maxWidth: "600px",
              width: "100%",
              padding: "48px",
              textAlign: "center",
              boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
              animation: "slideUp 0.4s ease-out",
            }}
          >
            <div
              style={{
                width: "120px",
                height: "120px",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 32px",
                fontSize: "56px",
                boxShadow: "0 10px 30px rgba(139, 69, 19, 0.3)",
              }}
            >
              <img
                src="/logo.png"
                alt="Logo"
                style={{ width: "74px", height: "74px" }}
              />
            </div>
            <h2
              style={{
                fontSize: "32px",
                fontWeight: "800",
                color: colors.text,
                marginBottom: "16px",
                lineHeight: "1.2",
              }}
            >
              Bienvenue sur Property Assist !
            </h2>
            <p
              style={{
                fontSize: "18px",
                color: colors.gray,
                lineHeight: "1.7",
                marginBottom: "32px",
              }}
            >
              Félicitations pour votre décision d'organiser vos biens
              immobiliers ! 🎉
              <br />
              <br />
              Profitez de votre coffre-fort sur le web pour constituer
              facilement vos dossiers et transférer les fichiers depuis et vers
              votre ordinateur. Retrouvez ensuite tous vos documents
              synchronisés avec votre application sur mobile.
              <br />
              <br />
              <strong style={{ color: colors.primary }}>
                Commencez par ajouter votre premier bien ci-dessous !
              </strong>
            </p>
            <button
              style={{
                ...styles.submitButton,
                fontSize: "16px",
                padding: "14px 28px",
              }}
              onClick={() => setShowOnboarding(false)}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow =
                  "0 6px 20px rgba(180, 132, 14, 0.4)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "none";
                e.currentTarget.style.boxShadow =
                  "0 2px 12px rgba(180, 132, 14, 0.25)";
              }}
            >
              C'est parti ! 🚀
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default withMobilePrompt(CoffreFortPage);
